import { useMemo } from 'react';
import * as z from 'zod';

import type { IndividualPaymentEditorViewModel } from '@/shared/api/generated';
import { individualPaymentSaveViewModelSchema } from '@/shared/api/generated';

/**
 * Campos do `IndividualPaymentSaveViewModel` que a tela controla.
 *
 * Derivado com `.pick()` do schema gerado pelo Kubb — nunca reescrito à mão. Os
 * campos ausentes desta lista não são editáveis na tela e voltam intactos no
 * `PUT` (ver `buildPlanSavePayload`).
 */
const editableShape = individualPaymentSaveViewModelSchema.pick({
  // Passthrough na edição (vêm do editor), campos de formulário na criação — onde
  // o operador é quem escolhe matrícula e plano financeiro do curso.
  courseRegistrationId: true,
  courseFinancialId: true,
  description: true,
  additionalInformation: true,
  hidePaymentPlan: true,
  scholarshipStatus: true,
  scholarshipTypeId: true,
  discountScholarshipPercentage: true,
  scholarshipReason: true,
  automaticGenerateInvoice: true,
  payAsCompany: true,
  financialAssociatedCompanyId: true,
  cnpj: true,
  ie: true,
  ieFree: true,
  companyName: true,
  companyContactName: true,
  companyContactEmail: true,
  companyContactPhone: true,
  countryCodeCompanyContact: true,
  thirdPayment: true,
  thirdName: true,
  thirdDocument: true,
  thirdEmail: true,
  thirdPhone: true,
  countryCodeThirdPhone: true,
  thirdCode: true,
  thirdAddress: true,
  valueRegistration: true,
  // Exposto apenas na criação (ver `PlanValuesTab`). Fica no schema para que o
  // `POST` possa carregá-lo; na edição continua sem `defaultValue`, e a chave
  // `undefined` some do corpo no `JSON.stringify` — o `PUT` segue como antes.
  generateEnrollmentParcel: true,
  totalValue: true,
  paymentMethod: true,
  expirationDate: true,
  discountPunctualPercentage: true,
  numberParcels: true,
  discounts: true,
  updatePlanParcels: true,
});

/**
 * Schema do formulário de edição de plano.
 *
 * O schema gerado é permissivo — o OpenAPI marca praticamente tudo como
 * opcional e nulo. Os quatro campos que a tela legada marca com asterisco são
 * endurecidos aqui, e as mensagens são **chaves de i18n**, resolvidas na UI.
 */
export const planFormSchema = editableShape.extend({
  /**
   * Pessoa selecionada no autocomplete — **campo só de UI**.
   *
   * Não pertence ao `IndividualPaymentSaveViewModel` e é removido do corpo antes
   * do `POST`: o backend deriva a pessoa do `courseRegistrationId`. Existe aqui
   * porque é ele que alimenta o lookup de matrículas, e guardá-lo fora do
   * formulário quebraria a limpeza em cascata.
   *
   * É `string` porque a chave do lookup é textual (`"USR260953"`); o id numérico
   * sai de `parseStudentKeyId`.
   */
  personId: z.string().optional(),
  description: z.string().min(1, 'planEdit.validation.descriptionRequired'),
  totalValue: z.number({ invalid_type_error: 'planEdit.validation.required' }),
  numberParcels: z.number({ invalid_type_error: 'planEdit.validation.required' }).int(),
  paymentMethod: z.number({ invalid_type_error: 'planEdit.validation.required' }).int(),
  // Continua opcional — plano sem bolsa não tem percentual —, mas quando existe
  // precisa ser um percentual de verdade.
  discountScholarshipPercentage: z
    .number()
    .min(0, 'planEdit.validation.scholarshipPercentageRange')
    .max(100, 'planEdit.validation.scholarshipPercentageRange')
    .nullish(),
  // 28 é o teto porque fevereiro existe: um vencimento em 29, 30 ou 31 não cai
  // em todo mês, e a cobrança escorregaria de forma imprevisível.
  expirationDate: z
    .number({ invalid_type_error: 'planEdit.validation.expirationDateRange' })
    .int()
    .min(1, 'planEdit.validation.expirationDateRange')
    .max(28, 'planEdit.validation.expirationDateRange'),
});

/** Valores do formulário de edição de plano. */
export type PlanFormValues = z.infer<typeof planFormSchema>;

/**
 * Schema do formulário de **criação** de plano.
 *
 * É o mesmo `planFormSchema` com um refinamento que endurece os três campos de
 * vínculo — pessoa, matrícula e plano de pagamento —, obrigatórios só aqui: na
 * edição eles chegam prontos do contrato e a tela nem os exibe.
 *
 * O endurecimento é feito por `superRefine`, e não por `.extend()`, de propósito:
 * `.extend()` trocaria os campos de opcionais para obrigatórios e o tipo inferido
 * deixaria de ser `PlanFormValues` — as duas telas precisam compartilhar um único
 * tipo de formulário para compartilharem as seções.
 */
export const planCreateSchema = planFormSchema.superRefine((values, ctx) => {
  if (values.personId === undefined || values.personId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['personId'],
      message: 'planCreate.validation.personRequired',
    });
  }

  if (typeof values.courseRegistrationId !== 'number') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['courseRegistrationId'],
      message: 'planCreate.validation.enrollmentRequired',
    });
  }

  if (typeof values.courseFinancialId !== 'number') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['courseFinancialId'],
      message: 'planCreate.validation.paymentPlanRequired',
    });
  }
});

/**
 * Valores iniciais do formulário de criação de plano.
 *
 * Diferente da edição, não há editor de onde partir: o vínculo começa vazio e os
 * valores monetários zerados. `totalValue`, `valueRegistration` e
 * `discountPunctualPercentage` são preenchidos depois, quando o operador escolhe
 * o plano de pagamento — ver `useCourseFinancialInfo`.
 *
 * @returns Valores iniciais do formulário.
 *
 * @example
 * ```tsx
 * const form = useForm({ resolver: zodResolver(planCreateSchema), defaultValues: buildPlanCreateDefaults() });
 * ```
 */
export function buildPlanCreateDefaults(): PlanFormValues {
  return {
    personId: '',
    courseRegistrationId: undefined,
    courseFinancialId: undefined,
    description: '',
    additionalInformation: '',
    hidePaymentPlan: false,
    scholarshipStatus: null,
    scholarshipTypeId: null,
    discountScholarshipPercentage: null,
    scholarshipReason: '',
    automaticGenerateInvoice: false,
    payAsCompany: false,
    financialAssociatedCompanyId: null,
    cnpj: '',
    ie: '',
    ieFree: false,
    companyName: '',
    companyContactName: '',
    companyContactEmail: '',
    companyContactPhone: '',
    countryCodeCompanyContact: '',
    thirdPayment: false,
    thirdName: '',
    thirdDocument: '',
    thirdEmail: '',
    thirdPhone: '',
    countryCodeThirdPhone: '',
    thirdCode: '',
    thirdAddress: undefined,
    valueRegistration: null,
    // Desmarcado por omissão: gerar a parcela de matrícula emite Nosso Número e
    // é irreversível, então é uma escolha explícita do operador, não um default.
    generateEnrollmentParcel: false,
    totalValue: 0,
    paymentMethod: 0,
    expirationDate: 0,
    discountPunctualPercentage: null,
    numberParcels: 0,
    discounts: [],
    // Não existe na criação — as parcelas são geradas pelo próprio `POST`.
    updatePlanParcels: false,
  };
}

/**
 * Monta os `defaultValues` do formulário a partir da resposta do editor.
 *
 * Exportada como função pura para que a montagem seja verificável sem montar o
 * formulário.
 *
 * @param editor - Resposta de `GET /financial-people/{id}/editor`.
 * @returns Valores iniciais do formulário.
 */
export function buildPlanFormDefaults(
  editor: IndividualPaymentEditorViewModel | undefined,
): PlanFormValues {
  return {
    // Não editáveis na edição, mas parte do formulário desde que a criação passou
    // a escolhê-los. Precisam vir preenchidos aqui: o corpo do `PUT` é montado a
    // partir dos valores do formulário, e deixá-los `undefined` zeraria o vínculo
    // do contrato no salvamento.
    ...(editor?.courseRegistrationId === undefined
      ? {}
      : { courseRegistrationId: editor.courseRegistrationId }),
    ...(editor?.courseFinancialId === undefined
      ? {}
      : { courseFinancialId: editor.courseFinancialId }),
    description: editor?.description ?? '',
    additionalInformation: editor?.additionalInformation ?? '',
    hidePaymentPlan: editor?.hidePaymentPlan ?? false,
    scholarshipStatus: editor?.scholarshipStatus ?? null,
    scholarshipTypeId: editor?.scholarshipTypeId ?? null,
    discountScholarshipPercentage: editor?.discountScholarshipPercentage ?? null,
    scholarshipReason: editor?.scholarshipReason ?? '',
    automaticGenerateInvoice: editor?.automaticGenerateInvoice ?? false,
    payAsCompany: editor?.payAsCompany ?? false,
    financialAssociatedCompanyId: editor?.financialAssociatedCompanyId ?? null,
    cnpj: editor?.cnpj ?? '',
    ie: editor?.ie ?? '',
    ieFree: editor?.ieFree ?? false,
    companyName: editor?.companyName ?? '',
    companyContactName: editor?.companyContactName ?? '',
    companyContactEmail: editor?.companyContactEmail ?? '',
    companyContactPhone: editor?.companyContactPhone ?? '',
    countryCodeCompanyContact: editor?.countryCodeCompanyContact ?? '',
    thirdPayment: editor?.thirdPayment ?? false,
    thirdName: editor?.thirdName ?? '',
    thirdDocument: editor?.thirdDocument ?? '',
    thirdEmail: editor?.thirdEmail ?? '',
    thirdPhone: editor?.thirdPhone ?? '',
    countryCodeThirdPhone: editor?.countryCodeThirdPhone ?? '',
    thirdCode: editor?.thirdCode ?? '',
    thirdAddress: editor?.thirdAddress,
    valueRegistration: editor?.valueRegistration ?? null,
    totalValue: editor?.totalValue ?? 0,
    paymentMethod: editor?.paymentMethod ?? 0,
    expirationDate: editor?.expirationDate ?? 0,
    discountPunctualPercentage: editor?.discountPunctualPercentage ?? null,
    numberParcels: editor?.numberParcels ?? 0,
    // `discounts[]` chega no editor como `IndividualPaymentEditorDiscountViewModel`
    // e sai no save como `IndividualPaymentSaveDiscountViewModel`: o formulário
    // guarda a forma de escrita, e a de leitura fica no editor cru para a tabela.
    discounts:
      editor?.discounts?.map(discount => ({
        id: discount.id,
        discountTypeId: discount.discountTypeId,
        percentage: discount.percentage,
        description: discount.description,
        associatedCompanyId: discount.associatedCompanyId,
        active: discount.active,
      })) ?? [],
    // Nunca vem marcado do backend: é uma decisão tomada a cada salvamento.
    updatePlanParcels: false,
  };
}

/**
 * Schema e valores iniciais do formulário de edição de plano.
 *
 * @param editor - Resposta do editor; `undefined` enquanto carrega.
 * @returns `{ schema, defaultValues }`
 *
 * @example
 * ```tsx
 * const { schema, defaultValues } = usePlanFormSchema(editor);
 * const form = useForm({ resolver: zodResolver(schema), defaultValues });
 * ```
 */
export function usePlanFormSchema(editor: IndividualPaymentEditorViewModel | undefined) {
  const defaultValues = useMemo(() => buildPlanFormDefaults(editor), [editor]);
  return { schema: planFormSchema, defaultValues } as const;
}
